import React from 'react';
import uuid from 'react-uuid';
import { FaChevronDown, FaChevronUp, FaTrash } from 'react-icons/fa';
import { BtnSimple } from '../form/btn.Simple';
import { Checkbox } from '../form/checkbox';
import {
  SettingsInput,
  SettingsRow,
  SettingsSelect,
  SettingsTextArea,
  WidgetSettingsRoot,
  WidgetSettingsSection,
} from '../form/settings.Widget';
import { ModalGlobal } from '../modal/modal.Global';

export const GeneratorSettingsLevel2 = props => {
  const {
    generatorId,
    profile,
    draft,
    updateDraft,
    canEdit,
    generatorOptions = [],
  } = props;
  const modalId = `generator-level2:${generatorId}:${profile.id}`;
  const [openLibraries, setOpenLibraries] = React.useState({});
  const [openOutputs, setOpenOutputs] = React.useState({});
  const DEFAULT_BUILDER = React.useMemo(
    () => ({
      libraryIds: [],
      separator: '',
      capitalize: true,
    }),
    [],
  );

  const profileLibraries = React.useMemo(
    () =>
      (draft.libraries || []).filter(lib =>
        Array.isArray(lib.profileIds)
          ? lib.profileIds.includes(profile.id)
          : false,
      ),
    [draft.libraries, profile.id],
  );
  const profileOptions = React.useMemo(
    () =>
      (draft.profiles || []).map(entry => ({
        id: entry.id,
        label: entry.label || 'Combinaison',
      })),
    [draft.profiles],
  );
  const profileLibrariesById = React.useMemo(() => {
    const map = {};
    profileLibraries.forEach(lib => {
      if (lib?.id) {
        map[lib.id] = lib;
      }
    });
    return map;
  }, [profileLibraries]);
  const generatorOptionById = React.useMemo(
    () =>
      Object.fromEntries(
        (Array.isArray(generatorOptions) ? generatorOptions : [])
          .filter(option => option?.id)
          .map(option => [option.id, option]),
      ),
    [generatorOptions],
  );

  const parseLibraryEntries = (value, separator) => {
    const raw = typeof value === 'string' ? value : '';
    if (separator === 'newline') {
      return raw
        .split('\n')
        .map(v => v.trim())
        .filter(Boolean);
    }
    return raw
      .split(/[,;\n]/)
      .map(v => v.trim())
      .filter(Boolean);
  };

  const updateLibraries = updater => {
    updateDraft(prev => ({
      ...prev,
      libraries: updater(Array.isArray(prev.libraries) ? prev.libraries : []),
    }));
  };

  const updateOutputs = updater => {
    updateDraft(prev => ({
      ...prev,
      outputs: updater(Array.isArray(prev.outputs) ? prev.outputs : []),
    }));
  };

  const updateLibraryById = (libraryId, updater) => {
    updateLibraries(current =>
      current.map(entry =>
        entry.id === libraryId
          ? typeof updater === 'function'
            ? updater(entry)
            : { ...entry, ...(updater || {}) }
          : entry,
      ),
    );
  };

  const profileOutputs = React.useMemo(
    () =>
      (draft.outputs || []).filter(output => {
        const profileIds = Array.isArray(output?.profileIds)
          ? output.profileIds
          : [];
        return profileIds.includes(profile.id);
      }),
    [draft.outputs, profile.id],
  );
  const profileItemLabelOutputIds = React.useMemo(
    () =>
      profileOutputs
        .filter(output => output?.target?.kind === 'item_label')
        .map(output => output.id),
    [profileOutputs],
  );
  const hasProfileItemLabel = profileItemLabelOutputIds.length > 0;

  React.useEffect(() => {
    if (profileItemLabelOutputIds.length <= 1) {
      return;
    }
    const [keepId, ...idsToDemote] = profileItemLabelOutputIds;
    if (!keepId || idsToDemote.length === 0) {
      return;
    }
    updateDraft(prev => ({
      ...prev,
      outputs: (prev.outputs || []).map(output => {
        if (!idsToDemote.includes(output.id)) {
          return output;
        }
        return {
          ...output,
          target: { kind: 'text' },
        };
      }),
    }));
  }, [profileItemLabelOutputIds, updateDraft]);

  const addOutput = kind => {
    const nextKind =
      kind === 'item_label' ? 'item_label' : kind === 'num' ? 'num' : 'text';
    if (nextKind === 'item_label' && hasProfileItemLabel) {
      return;
    }
    const outputId = uuid().replaceAll('-', '');
    updateDraft(prev => {
      const currentOutputs = Array.isArray(prev.outputs) ? prev.outputs : [];
      const nextIndex = currentOutputs.length + 1;
      return {
        ...prev,
        outputs: [
          ...currentOutputs,
          {
            id: outputId,
            label: nextKind === 'item_label' ? 'Nom' : `Texte ${nextIndex}`,
            target: { kind: nextKind },
            profileIds: [profile.id],
            pipeline: [],
            builder: DEFAULT_BUILDER,
            sourceGeneratorId: null,
            sourceControlIds: [],
            number: {
              min: 0,
              max: 10,
              step: 1,
            },
          },
        ],
      };
    });
  };

  const updateOutput = (outputId, updater) => {
    updateOutputs(current =>
      current.map(output => {
        if (output.id !== outputId) {
          return output;
        }
        const next =
          typeof updater === 'function'
            ? updater(output)
            : { ...output, ...(updater || {}) };
        return next;
      }),
    );
  };

  const updateOutputBuilder = (outputId, updater) => {
    updateOutput(outputId, output => {
      const currentBuilder =
        output?.builder && typeof output.builder === 'object'
          ? output.builder
          : DEFAULT_BUILDER;
      const nextBuilder =
        typeof updater === 'function'
          ? updater(currentBuilder)
          : { ...currentBuilder, ...(updater || {}) };
      return {
        ...output,
        builder: nextBuilder,
      };
    });
  };

  const removeOutput = outputId => {
    updateOutputs(current => current.filter(output => output.id !== outputId));
  };

  const moveOutput = (outputId, direction) => {
    updateOutputs(current => {
      const next = Array.isArray(current) ? [...current] : [];
      const scopedGlobalIndexes = next
        .map((entry, index) =>
          Array.isArray(entry?.profileIds) && entry.profileIds.includes(profile.id)
            ? index
            : -1,
        )
        .filter(index => index >= 0);
      const scopedIds = scopedGlobalIndexes.map(index => next[index]?.id);
      const currentScopedIndex = scopedIds.findIndex(id => id === outputId);
      if (currentScopedIndex === -1) {
        return current;
      }
      const targetScopedIndex =
        direction === 'up' ? currentScopedIndex - 1 : currentScopedIndex + 1;
      if (
        targetScopedIndex < 0 ||
        targetScopedIndex >= scopedGlobalIndexes.length
      ) {
        return current;
      }

      const fromGlobalIndex = scopedGlobalIndexes[currentScopedIndex];
      const toGlobalIndex = scopedGlobalIndexes[targetScopedIndex];
      const tmp = next[toGlobalIndex];
      next[toGlobalIndex] = next[fromGlobalIndex];
      next[fromGlobalIndex] = tmp;
      return next;
    });
  };

  const toggleOutput = outputId => {
    setOpenOutputs(prev => ({
      ...prev,
      [outputId]: prev?.[outputId] !== true,
    }));
  };

  const addLibrary = () => {
    const libId = uuid().replaceAll('-', '');
    updateDraft(prev => ({
      ...prev,
      libraries: [
        ...(prev.libraries || []),
        {
          id: libId,
          label: `Table ${((prev.libraries || []).length || 0) + 1}`,
          entries: [],
          entrySeparator: 'comma',
          profileIds: [profile.id],
        },
      ],
    }));
  };

  const removeLibrary = libId => {
    updateDraft(prev => {
      const currentLibraries = Array.isArray(prev.libraries) ? prev.libraries : [];
      const currentOutputs = Array.isArray(prev.outputs) ? prev.outputs : [];
      return {
        ...prev,
        libraries: currentLibraries.filter(lib => lib.id !== libId),
        outputs: currentOutputs.map(output => ({
          ...output,
          builder: {
            ...(output.builder || {}),
            libraryIds: (output?.builder?.libraryIds || []).filter(
              id => id !== libId,
            ),
          },
        })),
      };
    });
  };

  const toggleLibrary = libId => {
    setOpenLibraries(prev => ({
      ...prev,
      [libId]: !(prev[libId] === true),
    }));
  };

  return (
    <ModalGlobal
      idToOpen={modalId}
      title={`Génération • ${profile.label || 'Combinaison'}`}
      width={700}
      maxWidthClass='max-w-5xl'
    >
      <WidgetSettingsRoot>
        <WidgetSettingsSection
          title='tables de texte'
          help='Ajoute les tables de cette combinaison. Préfixe une entrée avec "-" pour la retirer. Exemple: -vétéran'
        >
          <div className='flex flex-col gap-05'>
            {profileLibraries.map(lib => (
              <div
                key={lib.id}
                className='rounded border border-primary border-opacity-20 bg-main bg-opacity-20 p-05 flex flex-col gap-05 min-w-0'
              >
                <div className='flex items-center gap-05'>
                  <button
                    type='button'
                    className='text-xs bg-main bg-opacity-40 rounded px-05 py-01 transition hover:opacity-60'
                    onClick={() => toggleLibrary(lib.id)}
                  >
                    {openLibraries[lib.id] === true ? (
                      <FaChevronUp />
                    ) : (
                      <FaChevronDown />
                    )}
                  </button>
                  <div className='flex-1'>
                    <SettingsInput
                      resetValue={lib.label || ''}
                      onChange={event => updateLibraryById(lib.id, { label: event.target.value })}
                    />
                  </div>
                  <div className='w-190'>
                    <SettingsSelect
                      value={profile.id}
                      onChange={event =>
                        updateLibraryById(lib.id, {
                          profileIds: [event.target.value],
                        })
                      }
                      disabled={!canEdit}
                    >
                      {profileOptions.map(option => (
                        <option
                          key={`${lib.id}:profile:${option.id}`}
                          value={option.id}
                        >
                          {option.label}
                        </option>
                      ))}
                    </SettingsSelect>
                  </div>
                  <div className='w-190'>
                    <SettingsSelect
                      value={
                        lib?.entrySeparator === 'newline'
                          ? 'newline'
                          : 'comma'
                      }
                      onChange={event =>
                        updateLibraryById(lib.id, {
                          entrySeparator:
                            event.target.value === 'newline'
                              ? 'newline'
                              : 'comma',
                        })
                      }
                      disabled={!canEdit}
                    >
                      <option value='comma'>virgule</option>
                      <option value='newline'>retour à la ligne</option>
                    </SettingsSelect>
                  </div>
                  <BtnSimple
                    className='text-xs bg-secondary-d border border-secondary-d'
                    onDoubleClick={() => removeLibrary(lib.id)}
                    disabled={!canEdit}
                    title='double-clic pour supprimer'
                  >
                    <FaTrash />
                  </BtnSimple>
                </div>
                {openLibraries[lib.id] === true && (
                  <SettingsTextArea
                    className='min-h-90 !m-0 !flex-none w-full max-w-full box-border !resize-y'
                    style={{ resize: 'vertical' }}
                    autoResize={false}
                    resetValue={
                      lib?.entrySeparator === 'newline'
                        ? (lib.entries || []).join('\n')
                        : (lib.entries || []).join(', ')
                    }
                    onChange={event =>
                      updateLibraryById(lib.id, entry => ({
                        ...entry,
                        entries: parseLibraryEntries(
                          event.target.value,
                          entry?.entrySeparator === 'newline'
                            ? 'newline'
                            : 'comma',
                        ),
                      }))
                    }
                  />
                )}
              </div>
            ))}
          </div>
          <div className='flex justify-end mt-05'>
            <BtnSimple
              className='text-xs bg-primary'
              onClick={addLibrary}
              disabled={!canEdit}
            >
              nouvelle table
            </BtnSimple>
          </div>
        </WidgetSettingsSection>

        <WidgetSettingsSection
          title='sorties générées'
          help='Ajoute un label et/ou plusieurs textes générés avec leurs propres règles. Pour les textes: utilise {{label_table}} dans le template markdown.'
        >
          <div className='flex flex-col gap-05'>
            {profileOutputs.map(output => {
              const builderLibraryIds = Array.isArray(output?.builder?.libraryIds)
                ? output.builder.libraryIds
                : [];
              const isNumberOutput = output?.target?.kind === 'num';
              const isTextOutput = output?.target?.kind !== 'item_label' && !isNumberOutput;
              const outputIndex = profileOutputs.findIndex(
                entry => entry.id === output.id,
              );

              return (
                <div
                  key={output.id}
                  className='rounded border border-primary border-opacity-20 bg-main bg-opacity-20 p-05 flex flex-col gap-05'
                >
                  <div className='flex items-center gap-05'>
                    <button
                      type='button'
                      className='text-xs bg-main bg-opacity-40 rounded px-05 py-01 transition hover:opacity-60'
                      onClick={() => toggleOutput(output.id)}
                    >
                      {openOutputs[output.id] === true ? (
                        <FaChevronUp />
                      ) : (
                        <FaChevronDown />
                      )}
                    </button>
                    <div className='flex-1'>
                      <SettingsInput
                        resetValue={output?.label || ''}
                        onChange={event =>
                          updateOutput(output.id, {
                            label: event.target.value,
                          })
                        }
                      />
                    </div>
                    <div className='w-150'>
                      <SettingsSelect
                        value={
                          output?.target?.kind === 'item_label'
                            ? 'item_label'
                            : output?.target?.kind === 'num'
                              ? 'num'
                            : 'text'
                        }
                        onChange={event => {
                          const nextKind =
                            event.target.value === 'item_label'
                              ? 'item_label'
                              : event.target.value === 'num'
                                ? 'num'
                              : 'text';
                          updateDraft(prev => {
                            const currentOutputs = Array.isArray(prev.outputs)
                              ? prev.outputs
                              : [];
                            const profileScoped = currentOutputs.filter(entry =>
                              Array.isArray(entry?.profileIds)
                                ? entry.profileIds.includes(profile.id)
                                : false,
                            );
                            const existingItemLabel = profileScoped.find(
                              entry =>
                                entry?.target?.kind === 'item_label' &&
                                entry.id !== output.id,
                            );
                            if (
                              nextKind === 'item_label' &&
                              existingItemLabel
                            ) {
                              return prev;
                            }
                            return {
                              ...prev,
                              outputs: currentOutputs.map(entry =>
                                entry.id === output.id
                                  ? {
                                      ...entry,
                                      target: { kind: nextKind },
                                      number: {
                                        min:
                                          Number.isFinite(
                                            Number(entry?.number?.min),
                                          )
                                            ? Number(entry.number.min)
                                            : 0,
                                        max:
                                          Number.isFinite(
                                            Number(entry?.number?.max),
                                          )
                                            ? Number(entry.number.max)
                                            : 10,
                                        step:
                                          Number.isFinite(
                                            Number(entry?.number?.step),
                                          ) &&
                                          Number(entry.number.step) > 0
                                            ? Number(entry.number.step)
                                            : 1,
                                      },
                                    }
                                  : entry,
                              ),
                            };
                          });
                        }}
                        disabled={!canEdit}
                      >
                        <option
                          value='item_label'
                          disabled={
                            hasProfileItemLabel &&
                            output?.target?.kind !== 'item_label'
                          }
                        >
                          label item
                        </option>
                        <option value='text'>texte</option>
                        <option value='num'>num</option>
                      </SettingsSelect>
                    </div>
                    <BtnSimple
                      className='text-xs bg-primary'
                      onClick={() => moveOutput(output.id, 'up')}
                      disabled={!canEdit || outputIndex <= 0}
                    >
                      ↑
                    </BtnSimple>
                    <BtnSimple
                      className='text-xs bg-primary'
                      onClick={() => moveOutput(output.id, 'down')}
                      disabled={!canEdit || outputIndex >= profileOutputs.length - 1}
                    >
                      ↓
                    </BtnSimple>
                    <BtnSimple
                      className='text-xs bg-secondary-d border border-secondary-d'
                      onDoubleClick={() => removeOutput(output.id)}
                      disabled={!canEdit}
                      title='double-clic pour supprimer'
                    >
                      <FaTrash />
                    </BtnSimple>
                  </div>

                  {openOutputs[output.id] === true &&
                    (isNumberOutput ? (
                    <div className='grid grid-cols-3 gap-05'>
                      <SettingsRow label='min'>
                        <SettingsInput
                          resetValue={String(
                            Number.isFinite(Number(output?.number?.min))
                              ? Number(output.number.min)
                              : 0,
                          )}
                          onChange={event =>
                            updateOutput(output.id, current => ({
                              ...current,
                              number: {
                                ...(current?.number || {}),
                                min: Number(event.target.value),
                              },
                            }))
                          }
                        />
                      </SettingsRow>
                      <SettingsRow label='max'>
                        <SettingsInput
                          resetValue={String(
                            Number.isFinite(Number(output?.number?.max))
                              ? Number(output.number.max)
                              : 10,
                          )}
                          onChange={event =>
                            updateOutput(output.id, current => ({
                              ...current,
                              number: {
                                ...(current?.number || {}),
                                max: Number(event.target.value),
                              },
                            }))
                          }
                        />
                      </SettingsRow>
                      <SettingsRow label='pas'>
                        <SettingsInput
                          resetValue={String(
                            Number.isFinite(Number(output?.number?.step)) &&
                              Number(output.number.step) > 0
                              ? Number(output.number.step)
                              : 1,
                          )}
                          onChange={event =>
                            updateOutput(output.id, current => ({
                              ...current,
                              number: {
                                ...(current?.number || {}),
                                step: Number(event.target.value),
                              },
                            }))
                          }
                        />
                      </SettingsRow>
                    </div>
                    ) : (
                    <div className='flex items-center gap-05'>
                      <div className='flex-1'>
                        <SettingsSelect
                          value='none'
                          onChange={event => {
                            const libId = event.target.value;
                            if (libId === 'none') {
                              return;
                            }
                            updateOutputBuilder(output.id, builder => ({
                              ...builder,
                              libraryIds: [...(builder.libraryIds || []), libId],
                            }));
                          }}
                          disabled={!canEdit || profileLibraries.length === 0}
                        >
                          <option value='none'>ajouter une table</option>
                          {profileLibraries.map(lib => (
                            <option
                              key={`${profile.id}:${output.id}:add:${lib.id}`}
                              value={lib.id}
                            >
                              {lib.label || lib.id}
                            </option>
                          ))}
                        </SettingsSelect>
                      </div>
                    </div>
                    ))}

                  {openOutputs[output.id] === true && !isNumberOutput && (
                    <SettingsRow label='source'>
                      <SettingsSelect
                        value={
                          typeof output?.sourceGeneratorId === 'string' &&
                          output.sourceGeneratorId.trim().length > 0
                            ? output.sourceGeneratorId
                            : 'local'
                        }
                        onChange={event =>
                          updateOutput(output.id, current => {
                            const nextSourceId =
                              event.target.value === 'local'
                                ? null
                                : event.target.value;
                            const availableControlIds = new Set(
                              (
                                generatorOptionById?.[nextSourceId]?.controls || []
                              ).map(control => control.id),
                            );
                            const nextSourceControlIds = Array.isArray(
                              current?.sourceControlIds,
                            )
                              ? current.sourceControlIds.filter(controlId =>
                                  availableControlIds.has(controlId),
                                )
                              : [];
                            return {
                              ...current,
                              sourceGeneratorId: nextSourceId,
                              sourceControlIds: nextSourceControlIds,
                            };
                          })
                        }
                        disabled={!canEdit}
                      >
                        <option value='local'>tables locales</option>
                        {generatorOptions.map(option => (
                          <option
                            key={`${output.id}:source-generator:${option.id}`}
                            value={option.id}
                          >
                            générateur: {option.label}
                          </option>
                        ))}
                      </SettingsSelect>
                    </SettingsRow>
                  )}
                  {openOutputs[output.id] === true &&
                    !isNumberOutput &&
                    typeof output?.sourceGeneratorId === 'string' &&
                    output.sourceGeneratorId.trim().length > 0 && (
                      <SettingsRow label='contrôles source'>
                        <div className='w-full flex flex-wrap gap-05'>
                          {(generatorOptionById?.[output.sourceGeneratorId]
                            ?.controls || []
                          ).map(control => {
                            const checked = Array.isArray(output?.sourceControlIds)
                              ? output.sourceControlIds.includes(control.id)
                              : false;
                            return (
                              <Checkbox
                                key={`${output.id}:source-control:${control.id}`}
                                checked={checked}
                                label={control.label || 'Option'}
                                onChange={nextChecked =>
                                  updateOutput(output.id, current => {
                                    const currentIds = Array.isArray(
                                      current?.sourceControlIds,
                                    )
                                      ? current.sourceControlIds
                                      : [];
                                    const nextIds = nextChecked
                                      ? Array.from(new Set([...currentIds, control.id]))
                                      : currentIds.filter(id => id !== control.id);
                                    return {
                                      ...current,
                                      sourceControlIds: nextIds,
                                    };
                                  })
                                }
                              />
                            );
                          })}
                          {(generatorOptionById?.[output.sourceGeneratorId]
                            ?.controls || []
                          ).length === 0 && (
                            <div className='text-xs text-contrast text-opacity-60'>
                              aucun contrôle disponible sur ce générateur
                            </div>
                          )}
                        </div>
                      </SettingsRow>
                    )}

                  {openOutputs[output.id] === true && !isNumberOutput && (
                    <div className='flex flex-col gap-03'>
                      {builderLibraryIds.map((libId, index) => {
                        const lib = profileLibrariesById[libId];
                        return (
                          <div
                            key={`${profile.id}:${output.id}:builder:${libId}:${index}`}
                            className='flex items-center gap-05 rounded border border-primary border-opacity-20 bg-main bg-opacity-20 p-05'
                          >
                            <div className='flex-1 text-sm truncate'>
                              {lib?.label || libId}
                            </div>
                            <BtnSimple
                              className='text-xs bg-primary'
                              onClick={() =>
                                updateOutputBuilder(output.id, builder => {
                                  const ids = [...(builder.libraryIds || [])];
                                  if (index <= 0) {
                                    return builder;
                                  }
                                  const tmp = ids[index - 1];
                                  ids[index - 1] = ids[index];
                                  ids[index] = tmp;
                                  return {
                                    ...builder,
                                    libraryIds: ids,
                                  };
                                })
                              }
                              disabled={!canEdit || index <= 0}
                            >
                              ↑
                            </BtnSimple>
                            <BtnSimple
                              className='text-xs bg-primary'
                              onClick={() =>
                                updateOutputBuilder(output.id, builder => {
                                  const ids = [...(builder.libraryIds || [])];
                                  if (index >= ids.length - 1) {
                                    return builder;
                                  }
                                  const tmp = ids[index + 1];
                                  ids[index + 1] = ids[index];
                                  ids[index] = tmp;
                                  return {
                                    ...builder,
                                    libraryIds: ids,
                                  };
                                })
                              }
                              disabled={!canEdit || index >= builderLibraryIds.length - 1}
                            >
                              ↓
                            </BtnSimple>
                            <BtnSimple
                              className='text-xs bg-secondary-d border border-secondary-d'
                              onDoubleClick={() =>
                                updateOutputBuilder(output.id, builder => ({
                                  ...builder,
                                  libraryIds: (builder.libraryIds || []).filter(
                                    (_, i) => i !== index,
                                  ),
                                }))
                              }
                              disabled={!canEdit}
                              title='double-clic pour supprimer'
                            >
                              <FaTrash />
                            </BtnSimple>
                          </div>
                        );
                      })}
                      {builderLibraryIds.length === 0 && (
                        <div className='text-xs text-contrast text-opacity-60'>
                          aucune table sélectionnée
                        </div>
                      )}
                    </div>
                  )}

                  {openOutputs[output.id] === true && !isNumberOutput && (
                    <SettingsRow label='séparateur'>
                      <SettingsInput
                        resetValue={output?.builder?.separator || ''}
                        onChange={event =>
                          updateOutputBuilder(output.id, {
                            separator: event.target.value,
                          })
                        }
                      />
                    </SettingsRow>
                  )}

                  {openOutputs[output.id] === true && isTextOutput && (
                    <SettingsRow label='template markdown'>
                      <SettingsTextArea
                        className='min-h-120'
                        style={{ resize: 'vertical' }}
                        autoResize={false}
                        resetValue={output?.markdownTemplate || ''}
                        onChange={event =>
                          updateOutput(output.id, {
                            markdownTemplate: event.target.value,
                          })
                        }
                      />
                    </SettingsRow>
                  )}

                  {openOutputs[output.id] === true && !isNumberOutput && (
                    <Checkbox
                      label='mettre une majuscule au début'
                      checked={output?.builder?.capitalize !== false}
                      onChange={checked =>
                        updateOutputBuilder(output.id, {
                          capitalize: checked,
                        })
                      }
                    />
                  )}
                </div>
              );
            })}
            {profileOutputs.length === 0 && (
              <div className='text-xs text-contrast text-opacity-60'>
                aucune sortie configurée
              </div>
            )}
          </div>
          <div className='flex items-center justify-end gap-05 mt-05'>
            <BtnSimple
              className='text-xs bg-primary'
              onClick={() => addOutput('item_label')}
              disabled={!canEdit || hasProfileItemLabel}
            >
              nouveau label
            </BtnSimple>
            <BtnSimple
              className='text-xs bg-primary'
              onClick={() => addOutput('text')}
              disabled={!canEdit}
            >
              nouveau texte
            </BtnSimple>
            <BtnSimple
              className='text-xs bg-primary'
              onClick={() => addOutput('num')}
              disabled={!canEdit}
            >
              nouveau num
            </BtnSimple>
          </div>
        </WidgetSettingsSection>
      </WidgetSettingsRoot>
    </ModalGlobal>
  );
};
